import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FilterPanel from '../../components/ui/FilterPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { fetchVideos, uploadVideo, getVideoSignedUrl, deleteVideo } from '../../api/videos';
import { apiFetch } from '../../api/prospects';

const Gallery = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const canManageVideos =
    currentUser?.permission === 'admin' || currentUser?.permission === 'team';

  const loadVideos = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchVideos();
      setVideos(res?.data?.videos || []);
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les vidéos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const me = await apiFetch('/me');
        setCurrentUser(me?.data?.user || null);
      } catch (e) {
        console.error(e);
        setCurrentUser(null);
      }
      await loadVideos();
    };
    init();
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!title.trim() || !file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('file', file);
      const res = await uploadVideo(formData);
      const v = res?.data?.video;
      if (v) {
        setVideos((prev) => [...prev, v]);
        setTitle('');
        setFile(null);
      }
    } catch (e) {
      console.error(e);
      setError("Erreur lors de l'upload de la vidéo.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Galerie vidéos - CRM</title>
      </Helmet>
      <Header />
      <Breadcrumb />
      <FilterPanel />

      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Galerie vidéos
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Uploadez vos vidéos de campagne et visualisez-les dans une galerie.
              </p>
            </div>
          </div>

          {canManageVideos && (
            <form
              onSubmit={handleUpload}
              className="bg-card rounded-lg p-4 md:p-6 shadow-elevation-2 border border-border mb-6 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1 block">
                    Titre de la vidéo *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Nom de la vidéo"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground mb-1 block">
                    Fichier vidéo *
                  </label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleFileChange}
                    className="w-full text-sm"
                    required
                  />
                </div>
                <div className="flex md:justify-end">
                  <Button
                    type="submit"
                    iconName="Upload"
                    iconPosition="left"
                    disabled={uploading}
                  >
                    {uploading ? 'Upload en cours...' : 'Uploader la vidéo'}
                  </Button>
                </div>
              </div>
              {error && <p className="text-sm text-error">{error}</p>}
            </form>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement des vidéos...</p>
        ) : videos.length === 0 ? (
          <div className="bg-card rounded-lg p-12 text-center border border-border">
            <Icon name="Video" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Aucune vidéo dans la galerie
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Uploadez une première vidéo pour alimenter votre galerie.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {videos.map((video) => (
              <div
                key={video._id}
                className="bg-card rounded-lg p-4 shadow-elevation-1 hover:shadow-elevation-2 transition-smooth border border-border flex flex-col items-stretch text-left relative"
              >
                {canManageVideos && (
                  <button
                    type="button"
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-background/80 hover:bg-destructive/10 transition-smooth"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm('Supprimer cette vidéo ?')) return;
                      try {
                        await deleteVideo(video._id);
                        setVideos((prev) => prev.filter((v) => v._id !== video._id));
                      } catch (err) {
                        console.error(err);
                        setError("Erreur lors de la suppression de la vidéo.");
                      }
                    }}
                  >
                    <Icon name="Trash2" size={14} className="text-error" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    setPreviewError(null);
                    setPreviewLoading(true);
                    setPreviewModal({ ...video, url: null });
                    try {
                      const res = await getVideoSignedUrl(video._id);
                      const url = res?.data?.url;
                      setPreviewModal((prev) =>
                        prev && prev._id === video._id ? { ...prev, url } : prev,
                      );
                    } catch (e) {
                      console.error(e);
                      setPreviewError("Impossible de charger la vidéo.");
                    } finally {
                      setPreviewLoading(false);
                    }
                  }}
                  className="flex flex-col items-stretch text-left"
                >
                  <div className="aspect-video bg-muted rounded-md flex items-center justify-center mb-3 overflow-hidden">
                    <Icon name="PlayCircle" size={40} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground line-clamp-2 mb-1">
                    {video.title}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Ajoutée le{' '}
                    {video.createdAt
                      ? new Date(video.createdAt).toLocaleDateString('fr-FR')
                      : ''}
                  </p>
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {previewModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between border-b border-border p-4">
              <h2 className="text-lg font-semibold text-foreground line-clamp-1">
                {previewModal.title}
              </h2>
              <button
                type="button"
                onClick={() => setPreviewModal(null)}
                className="p-2 rounded-lg hover:bg-muted transition-smooth"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-4">
              {previewLoading && (
                <p className="text-sm text-muted-foreground mb-2">
                  Chargement de la vidéo...
                </p>
              )}
              {previewError && (
                <p className="text-sm text-error mb-2">
                  {previewError}
                </p>
              )}
              <div className="w-full aspect-video bg-black rounded-lg overflow-hidden mb-4 flex items-center justify-center">
                {previewModal.url ? (
                  <video
                    src={previewModal.url}
                    controls
                    className="w-full h-full object-contain bg-black"
                  />
                ) : (
                  <Icon name="Loader2" size={32} className="animate-spin text-muted-foreground" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;

